import { useState } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Button, Grid } from "@mui/material";
import { Switch, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { v4 as uuid } from "uuid";

import { deepCopyObject } from "../../utils/helpers";
import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogMessage from "../ui/omniDialogMessage";
import DialogTitle from "../ui/omniDialogTitle";
import OmniResponsiveButton from "../ui/omniResponsiveButton";
import OmniTextField from "../ui/omniTextField";
import AlertDialog from "./alertDialog";

const BotpressBotDialog = props => {
  const {
    open,
    dialogTitle = "Botpress Definition",
    dialogMessage = null,
    okMessage = "Ok",
    cancelMessage = "Cancel",
    deleteMessage = "delete",
    maxWidth = "md",
    fullWidth = true,
    rowCount = 3,
    omniODM, //omni Data Model, a root level object representation, Pass in a COPY, the only way this works
  } = props;

  const theme = useTheme();

  const [editedValues] = useState(deepCopyObject(omniODM));
  const [workspaceId, setWorkspaceId] = useState(omniODM.omniData.workspaceId);
  const [botId, setBotId] = useState(omniODM.omniData.botId);
  const [webchatClientId, setWebchatClientId] = useState(omniODM.omniData.webchatClientId);
  const [accessToken, setAccessToken] = useState(omniODM.omniData.accessToken);
  const [name, setName] = useState(omniODM.omniData.name);
  const [description, setDescription] = useState(omniODM.omniData.description);
  const [userData, setUserData] = useState(omniODM.omniData.userData);
  const [botData, setBotData] = useState(omniODM.omniData.botData);
  const [isConversationAnalytics, setIsConversationAnalytics] = useState(
    omniODM.omniData.isConversationAnalytics,
  );

  const [showConfirm, setShowConfirm] = useState(false);

  const onDeleteHandler = () => {
    props.onDelete(omniODM);
  };

  const onAcceptHandler = () => {
    editedValues.omniData.workspaceId = workspaceId;
    editedValues.omniData.botId = botId;
    editedValues.omniData.webchatClientId = webchatClientId;
    editedValues.omniData.accessToken = accessToken;
    editedValues.omniData.name = name;
    editedValues.omniData.description = description;
    editedValues.omniData.userData = userData;
    editedValues.omniData.botData = botData;
    editedValues.omniData.isConversationAnalytics = isConversationAnalytics;

    if (editedValues._id == null) {
      editedValues._id = uuid();
    }
    props.onAccept(editedValues);
  };

  const onCloseHandler = () => {
    props.onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onCloseHandler}
      disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogTitle onClose={onCloseHandler}>{dialogTitle}</DialogTitle>
      <DialogContent>
        <Grid container item spacing={1}>
          <Grid item size={{ xs: 6 }}>
            {dialogMessage && <DialogMessage>{dialogMessage}</DialogMessage>}
            <OmniTextField
              label="Test Harness Name"
              type="textInput"
              name={uuid()} //prevents AutoComplete Suggestions
              variant="outlined"
              value={name}
              onChange={event => {
                setName(event.target.value);
              }}
              autoFocus
              placeholder={"Bot Alias"}
              required
            />

            <OmniTextField
              label="Description"
              type="textInput"
              value={description}
              name={uuid()} //prevents AutoComplete Suggestions
              variant="outlined"
              onChange={event => setDescription(event.target.value)}
              placeholder={"Bot Description"}
              multiline
              rows={rowCount}
            />
            <OmniTextField
              label="Workspace ID"
              type="textInput"
              name={uuid()}
              variant="outlined"
              value={workspaceId}
              onChange={event => {
                setWorkspaceId(event.target.value);
              }}
              placeholder={"Copy in the Botpress Workplace ID"}
              required
            />
            <OmniTextField
              label="Bot ID"
              type="textInput"
              name={uuid()}
              variant="outlined"
              value={botId}
              onChange={event => {
                setBotId(event.target.value);
              }}
              placeholder={"The Bot ID is in the URL when editing the bot."}
              required
            />
            <OmniTextField
              label="Webchat Client ID"
              type="textInput"
              name={uuid()}
              variant="outlined"
              value={webchatClientId}
              onChange={event => {
                setWebchatClientId(event.target.value);
              }}
              placeholder={"The webchat client ID from the webchat configuration"}
              required
            />
            <OmniTextField
              label="Access Token"
              type="textInput"
              name={uuid()}
              variant="outlined"
              value={accessToken}
              onChange={event => {
                setAccessToken(event.target.value);
              }}
              placeholder={"The access token for the bot."}
              required
            />
          </Grid>

          <Grid item size={{ xs: 6 }} xs={{ paddingLeft: theme.spacing(1) }}>
            <Grid item size={{ xs: 12 }}>
              <OmniTextField
                label="Default User Data - JSON"
                type="textInput"
                name={uuid()}
                variant="outlined"
                value={JSON.stringify(userData, null, 2)}
                onChange={event => {
                  setUserData(JSON.parse(event.target.value));
                }}
                multiline
                rows={5}
              />
            </Grid>

            <Grid item size={{ xs: 12 }}>
              <OmniTextField
                label="Default Bot Data - JSON"
                type="textInput"
                name={uuid()}
                variant="outlined"
                value={JSON.stringify(botData, null, 2)}
                onChange={event => {
                  setBotData(JSON.parse(event.target.value));
                }}
                multiline
                rows={6}
              />
            </Grid>
            <Grid item container size={{ xs: 12 }}>
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "row",
                  marginTop: "4px",
                  justifyContent: "left",
                }}
              >
                <Switch
                  checked={isConversationAnalytics}
                  onChange={() => {
                    setIsConversationAnalytics(val => !val);
                  }}
                />
                <Typography variant="body2" sx={{ marginTop: "8px" }}>
                  Use for Conversation Analytics
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Box
          sx={{
            paddingLeft: theme.spacing(0.5),
            paddingBottom: 0,
            display: "flex",
            flexDirection: "row",
            userSelect: "none",
            webkitUserSelect: "none",
            position: "relative",
            flexGrow: 1,
          }}
        >
          {omniODM._id != null && (
            <OmniResponsiveButton
              icon={<DeleteIcon />}
              onClick={() => setShowConfirm(true)}
              buttonText={deleteMessage}
              tooltipTitle=""
            />
          )}
        </Box>
        <Box
          sx={{
            flexGrow: 0,
            flexShrink: 1,
          }}
        >
          {cancelMessage && (
            <Button
              onClick={onCloseHandler}
              color="secondary"
              variant="contained"
              sx={{ marginRight: theme.spacing(2) }}
            >
              {cancelMessage}
            </Button>
          )}

          <Button
            onClick={onAcceptHandler}
            color="secondary"
            disabled={!workspaceId || !botId || !webchatClientId || !name}
            variant="contained"
          >
            {okMessage}
          </Button>
        </Box>
      </DialogActions>

      {showConfirm && (
        <AlertDialog
          open={showConfirm}
          dialogTitle={`Confirm`}
          dialogMessage={"Are you sure you want to delete this note?"}
          onAccept={() => {
            onDeleteHandler();
            setShowConfirm(false);
          }}
          onClose={() => {
            setShowConfirm(false);
          }}
        />
      )}
    </Dialog>
  );
};

export default BotpressBotDialog;
